<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import MetaInstabilityBanner from './components/app/MetaInstabilityBanner.vue';
// Aceleres: alerta global de possiveis faltas pausado temporariamente.
// import AceleresAbsenceAlertCenter from './components/app/AceleresAbsenceAlertCenter.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import ConclusionResultModal from './components/widgets/conversation/ConclusionResultModal.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { resolveLocale } from 'shared/constants/enabledLocales';
import { refreshBrowserPushState } from './helper/notificationPreferences';
import { activeTrainingConversationId } from './helper/trainingConversation';

const ACELERES_MOBILE_SESSION_DEDUPE_MS = 2000;
const ACELERES_MOBILE_QUERY = '(max-width: 767px)';
const ACELERES_EXTERNAL_URL_SELECTOR = 'a[href], [data-aceleres-external-url]';

const isMobileSessionRuntime = () =>
  window.matchMedia(ACELERES_MOBILE_QUERY).matches;

const isInstagramUrl = value => {
  try {
    const hostname = new URL(value, window.location.origin).hostname;
    return hostname === 'instagram.com' || hostname.endsWith('.instagram.com');
  } catch {
    return false;
  }
};

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    ConclusionResultModal,
    PendingEmailVerificationBanner,
    MetaInstabilityBanner,
    // Aceleres: alerta global de possiveis faltas pausado temporariamente.
    // AceleresAbsenceAlertCenter,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      reconnectService: null,
      hasFinishedZoraxyLoading: false,
      isWaitingForZoraxyShell: false,
      initialDashboardBootPromise: null,
      zoraxyLoadingRafId: null,
      zoraxyLoadingMaxMs: 15000,
      zoraxyRouteLoadingMaxMs: 15000,
      initialZoraxyPath: window.location.pathname,
      aceleresSessionCheckInFlight: false,
      lastAceleresSessionCheckAt: 0,
      isReturningFromMobileInstagram: false,
    };
  },
  computed: {
    ...mapGetters({
      isRTL: 'accounts/isRTL',
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    isDashboardReady() {
      return (
        !this.authUIFlags.isFetching && !this.accountUIFlags.isFetchingItem
      );
    },
    dailyTrainingConversationId() {
      return activeTrainingConversationId(this.currentUser);
    },
    dailyTrainingAccountId() {
      const id = Number(
        this.currentUser?.custom_attributes?.daily_training_account_id || 0
      );
      return Number.isFinite(id) && id > 0 ? id : null;
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
    isDashboardReady: {
      immediate: true,
      handler(isReady) {
        if (!isReady || this.hasFinishedZoraxyLoading) {
          return;
        }

        this.scheduleZoraxyLoadingFinish();
      },
    },
    dailyTrainingConversationId: {
      immediate: true,
      handler() {
        this.enforceDailyTrainingRoute();
      },
    },
    '$route.fullPath'() {
      this.enforceDailyTrainingRoute();
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // Idioma da interface: ver shared/constants/enabledLocales.
    this.setLocale();
    // O logout noutra aba chega por push: o Rails emite 'user:logout' no
    // pubsub do utilizador no mesmo instante em que revoga os tokens
    // (Internal::SsoLogoutController), e o actionCable.js ja o encaminha para
    // AuthAPI.logout(). Isso e mais rapido do que qualquer intervalo e nao
    // custa requisicao nenhuma — o antigo heartbeat de 5s fazia ~720 pedidos
    // por hora em cada aba aberta.
    //
    // A verificacao pontual abaixo continua a existir como rede: cobre a aba
    // que estava suspensa (o browser fecha o websocket em separadores
    // adormecidos) e o caso de o socket ter caido durante o logout.
    window.addEventListener('focus', this.checkAceleresSession);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener(
      'click',
      this.markMobileInstagramNavigation,
      true
    );
  },
  unmounted() {
    window.removeEventListener('focus', this.checkAceleresSession);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    document.removeEventListener(
      'click',
      this.markMobileInstagramNavigation,
      true
    );
    if (this.zoraxyLoadingRafId) {
      window.cancelAnimationFrame(this.zoraxyLoadingRafId);
      this.zoraxyLoadingRafId = null;
    }
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    markMobileInstagramNavigation(event) {
      if (!isMobileSessionRuntime()) return;

      const link = event.target?.closest?.(ACELERES_EXTERNAL_URL_SELECTOR);
      const externalUrl =
        link?.dataset?.aceleresExternalUrl || link?.getAttribute?.('href');
      if (isInstagramUrl(externalUrl)) {
        this.isReturningFromMobileInstagram = true;
      }
    },
    checkAceleresSession() {
      if (window.aceleresSessionTerminationStarted) return;

      // Abas em segundo plano nao consultam a sessao. No telemovel, foco e
      // visibilidade costumam disparar juntos ao retomar; a janela curta evita
      // duas verificacoes consecutivas sem atrasar a validacao necessaria.
      if (
        document.visibilityState !== 'visible' ||
        this.aceleresSessionCheckInFlight
      ) {
        return;
      }

      // Ao abrir um post/reel, o navegador mobile suspende o Zoraxy e dispara
      // `focus` + `visibilitychange` quando o Instagram devolve o controlo.
      // Essa retomada nao e uma nova navegacao nem uma troca de sessao. Consumir
      // apenas a primeira validacao preserva a conversa; as chamadas seguintes
      // e o websocket continuam a detectar uma revogacao real normalmente.
      if (isMobileSessionRuntime() && this.isReturningFromMobileInstagram) {
        this.isReturningFromMobileInstagram = false;
        this.lastAceleresSessionCheckAt = Date.now();
        return;
      }

      if (
        isMobileSessionRuntime() &&
        Date.now() - this.lastAceleresSessionCheckAt <
          ACELERES_MOBILE_SESSION_DEDUPE_MS
      ) {
        return;
      }

      this.aceleresSessionCheckInFlight = true;
      if (isMobileSessionRuntime()) {
        this.lastAceleresSessionCheckAt = Date.now();
      }
      // O modulo `auth` do store NAO e namespaced (store/modules/auth.js
      // exporta sem `namespaced: true`), entao 'auth/validityCheck' e um tipo
      // desconhecido: o Vuex devolve `undefined` e o `.finally` estourava.
      // Como o erro acontecia depois de marcar `inFlight`, a flag ficava presa
      // em true e o heartbeat de 5s virava no-op silencioso — a sessao
      // encerrada noutro separador nunca era detectada.
      this.store.dispatch('validityCheck').finally(() => {
        this.aceleresSessionCheckInFlight = false;
      });
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkAceleresSession();
      }
    },
    enforceDailyTrainingRoute() {
      const conversationId = this.dailyTrainingConversationId;
      const accountId =
        this.dailyTrainingAccountId || Number(this.currentAccountId || 0);
      if (!conversationId || !accountId) return;

      const target = `/app/accounts/${accountId}/conversations/${conversationId}`;
      if (!this.$route.fullPath.startsWith(target)) {
        this.router.replace(target);
      }
    },
    hasVisibleZoraxyShell() {
      const appRoot = document.getElementById('app');
      const sidebar = appRoot?.querySelector('aside');
      const main = appRoot?.querySelector('main');

      if (!appRoot || !sidebar || !main) {
        return false;
      }

      const sidebarRect = sidebar.getBoundingClientRect();
      const mainRect = main.getBoundingClientRect();

      if (
        sidebarRect.width < 40 ||
        sidebarRect.height < 120 ||
        mainRect.width < 120 ||
        mainRect.height < 120
      ) {
        return false;
      }

      return Array.from(main.children).some(child => {
        if (!(child instanceof HTMLElement)) {
          return false;
        }

        if (child.offsetParent === null) {
          return false;
        }

        const rect = child.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    },
    isVisibleElement(selector) {
      const element = document.querySelector(selector);
      if (!(element instanceof HTMLElement) || element.offsetParent === null) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    },
    isAnyVisibleElement(selector) {
      return Array.from(document.querySelectorAll(selector)).some(element => {
        if (
          !(element instanceof HTMLElement) ||
          element.offsetParent === null
        ) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    },
    requiresInitialRouteContentGuard() {
      return /\/app\/accounts\/\d+\/(?:configuracoes\/equipes|settings\/teams(?:\/list)?)\/?$/.test(
        this.initialZoraxyPath
      );
    },
    hasResolvedInitialRouteContent() {
      if (!this.requiresInitialRouteContentGuard()) {
        return true;
      }

      const routePath = this.$route?.path || window.location.pathname;
      const isTeamsRoute =
        /\/app\/accounts\/\d+\/configuracoes\/equipes\/?$/.test(routePath) ||
        /\/app\/accounts\/\d+\/settings\/teams(?:\/list)?\/?$/.test(routePath);

      if (!isTeamsRoute) {
        return false;
      }

      const hasSettingsShell =
        this.isVisibleElement('.settings-wrapper--teams') &&
        this.isVisibleElement('.settings-wrapper__content--teams');
      const hasLoadedTeamsContent = this.isAnyVisibleElement(
        '.aceleres-teams-page__body, .settings-wrapper__content--teams main > p'
      );
      const hasConversationContent = this.isAnyVisibleElement(
        '.painel-atendimento, .conversation-details-wrap'
      );

      return (
        hasSettingsShell && hasLoadedTeamsContent && !hasConversationContent
      );
    },
    scheduleZoraxyLoadingFinish() {
      if (this.hasFinishedZoraxyLoading || this.isWaitingForZoraxyShell) {
        return;
      }

      this.isWaitingForZoraxyShell = true;
      const { __zoraxyLoadingStartedAt: zoraxyLoadingStartedAt } = window;
      const startedAt = Number(zoraxyLoadingStartedAt) || Date.now();
      const maxWaitMs = this.requiresInitialRouteContentGuard()
        ? this.zoraxyRouteLoadingMaxMs
        : this.zoraxyLoadingMaxMs;
      let stableFrames = 0;

      const finalizeLoading = () => {
        if (this.hasFinishedZoraxyLoading) {
          this.isWaitingForZoraxyShell = false;
          this.zoraxyLoadingRafId = null;
          return;
        }

        this.isWaitingForZoraxyShell = false;
        this.zoraxyLoadingRafId = null;

        this.hasFinishedZoraxyLoading = true;
        window.finishZoraxyLoading?.();
      };

      const checkShell = () => {
        if (this.hasFinishedZoraxyLoading) {
          this.isWaitingForZoraxyShell = false;
          this.zoraxyLoadingRafId = null;
          return;
        }

        const shellReady =
          this.hasVisibleZoraxyShell() && this.hasResolvedInitialRouteContent();
        stableFrames = shellReady ? stableFrames + 1 : 0;

        if (stableFrames >= 2 || Date.now() - startedAt >= maxWaitMs) {
          // Conversation data is warmed in the background by ChatList. The
          // global overlay must only protect the first visible shell; waiting
          // for those requests here blocks every tab and modal on slow accounts.
          finalizeLoading();
          return;
        }

        this.zoraxyLoadingRafId = window.requestAnimationFrame(checkShell);
      };

      this.$nextTick(() => {
        this.zoraxyLoadingRafId = window.requestAnimationFrame(checkShell);
      });
    },
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    async loadNotificationPreferences() {
      refreshBrowserPushState();

      await Promise.all([
        this.$store.dispatch('userNotificationSettings/get'),
        this.$store.dispatch('inboxNotificationPreferences/get'),
      ]);

      // Uma falha de rede aqui silenciaria todos os alertas da sessão, então
      // vale uma segunda tentativa antes de desistir.
      const pushFlags =
        this.$store.getters['userNotificationSettings/getSelectedPushFlags'];
      if (!Array.isArray(pushFlags)) {
        window.setTimeout(() => {
          this.$store.dispatch('userNotificationSettings/get');
          this.$store.dispatch('inboxNotificationPreferences/get');
        }, 5000);
      }
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale() {
      // O idioma ativo sai sempre de shared/constants/enabledLocales: um locale
      // salvo na conta que ainda nao esteja liberado cai no padrao (pt_BR),
      // para nunca deixar a interface meio traduzida.
      const { locale } = this.getAccount(this.currentAccountId) || {};
      this.$root.$i18n.locale = resolveLocale(locale);
    },
    async initializeAccount() {
      const accountId = this.currentAccountId;

      if (
        this.initialDashboardBootPromise?.accountId === accountId &&
        this.initialDashboardBootPromise?.promise
      ) {
        return this.initialDashboardBootPromise.promise;
      }

      const bootPromise = this.initializeAccountBoot(accountId);
      this.initialDashboardBootPromise = {
        accountId,
        promise: bootPromise,
      };

      return bootPromise;
    },
    async initializeAccountBoot(accountId) {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId,
      });
      // As Preferências de Notificação precisam estar em memória antes do
      // primeiro evento do websocket: sem elas nenhum alerta é exibido.
      this.loadNotificationPreferences().catch(() => {});
      const restoredConversations = await this.$store.dispatch(
        'restoreConversationPrefetchCache',
        {
          accountId,
          userId: this.currentUser?.id,
        }
      );

      if (
        Array.isArray(restoredConversations) &&
        restoredConversations.length
      ) {
        const prefetchRestoredConversations = () => {
          this.$store
            .dispatch('prefetchRecentConversationDetails', {
              conversations: restoredConversations,
              // Restored snapshots already contain their recent messages, but
              // full conversation details remain lazy. Keep boot cache-first
              // and hydrate only the first eligible conversation.
              limit: 1,
              forceRefresh: false,
            })
            .catch(() => {});
        };

        if (Reflect.get(window, '__zoraxyLoadingActive')) {
          window.addEventListener(
            'zoraxy:loading-finished',
            prefetchRestoredConversations,
            { once: true }
          );
        } else if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(prefetchRestoredConversations, {
            timeout: 1500,
          });
        } else {
          window.setTimeout(prefetchRestoredConversations, 250);
        }
      }
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // Idioma da interface: ver shared/constants/enabledLocales.
      this.setLocale();
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <MetaInstabilityBanner />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <!-- Aceleres: alerta global de possiveis faltas pausado temporariamente.
    <AceleresAbsenceAlertCenter v-if="currentAccountId" />
    -->
    <ConclusionResultModal />
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
